import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { OrderDetailPopup } from 'src/app/admin-dashboard/orders/order-detail-popup/order-detail-popup';
import { AuthService } from '../../services/auth.service';
import { OrdersService, cardLabel, orderTimeOf } from 'src/app/services/orders.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { delay, map, shareReplay, take } from 'rxjs/operators';
import { SystemInfoService } from 'src/app/services/system-info.service';
import { ColumnSetting } from 'src/app/layout/table-standard-layout/table-standard-layout.component';
import { ActionButton, ActionButtonEvent } from 'src/app/layout/action-button-toolbar/action-button-toolbar.component';
import { FormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

// Shows the invalid (red) state as soon as there's a value, instead of
// Material's default of waiting until the field is blurred/touched - the
// user wants live feedback while typing, not only after leaving the field.
class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid && control.value);
  }
}


@Component({
  selector: 'app-order-report',
  templateUrl: './order-report.component.html',
  styleUrls: ['./order-report.component.css']
})
export class OrderReportComponent implements OnInit, OnDestroy {
  inputDateFrom;
  inputDateTo;
  // Cleared by default: the initial view still loads today's orders (see
  // ngOnInit), but the time-period filter itself starts unset so that a
  // search made without picking a period isn't silently scoped to today.
  timeSelected: string = '';
  orderNumberSearch: string = '';
  // Last four digits of the paying card (Stripe orders: web and kiosk tablet).
  cardSearch: string = '';
  // What the table shows while a card search is applied, for the banner.
  cardSearchLabel: string = '';
  // True once an order-number search has actually been applied (Enter
  // pressed) - while true, the System/Time period/Max orders controls are
  // disabled and companies$ shows only the matched order(s), since those
  // other filters have no bearing on an exact order lookup.
  orderNumberSearchActive: boolean = false;
  orderNumberErrorMatcher = new ImmediateErrorStateMatcher();
  isLoading: boolean = false;

  maxOrdersOptions: number[] = [200, 500, 1000, 5000, 10000];
  maxOrders: number = 200;

  columnSettings: ColumnSetting[] = [
    { columnId: 'orderNo', name: 'orderNo' },
    { columnId: 'name', name: 'System' },
    { columnId: 'product', name: 'Product' },
    { columnId: 'timestamp', name: 'Time' },
    { columnId: 'paymentStatus', name: 'Payment' },
    { columnId: 'status', name: 'Order' },
    { columnId: 'user', name: 'User', hideOnMobile: true },
    { columnId: 'pay', name: 'Type' },
    { columnId: 'card', name: 'Card', hideOnMobile: true },
    { columnId: 'total', name: 'Amount' }
  ];
  searchFields: string[] = ['name', 'orderNo', 'orderId', 'total', 'product', 'user', 'card'];

  rowActionButtons: ActionButton[];
  updateCompany: EventEmitter<ActionButtonEvent> = new EventEmitter<ActionButtonEvent>();
  sameCard: EventEmitter<ActionButtonEvent> = new EventEmitter<ActionButtonEvent>();
  private sameCardSub: Subscription;

  private rawOrders$ = new BehaviorSubject<any[]>([]);
  // The staged System choice - just what's currently picked in the
  // autocomplete, not yet applied to the table (see appliedSystem$ below).
  selectedSystem$ = new BehaviorSubject<string>('');
  // What companies$ actually filters by - only updated by onSubmit()
  // (clicking Search) or clearSystemFilter(), so picking a system doesn't
  // change what's currently shown until it's actually applied.
  private appliedSystem$ = new BehaviorSubject<string>('');

  // True once the user has actually applied a time period or custom range by
  // clicking Search (not merely picked one in the dropdown - that alone
  // fetches nothing). While false, a filter is broadened to any day instead
  // of staying scoped to whatever was loaded by default (today).
  private dateFilterActive = false;

  // null = no order-number search applied (normal System filtering below);
  // an array = show exactly these matches instead, ignoring System/Time
  // period entirely, since neither is meaningful for an exact order lookup.
  private orderNumberResults$ = new BehaviorSubject<any[] | null>(null);

  // Free-text search is handled by app-table-standard-layout's own built-in
  // search box (see [searchFields] on it in the template) rather than here.
  companies$ = combineLatest([this.rawOrders$, this.appliedSystem$, this.orderNumberResults$]).pipe(
    map(([orders, system, orderNumberResults]) => {
      if (orderNumberResults !== null) return orderNumberResults;
      if (!system) return orders;
      const ids = this.systemIdsByName[system] ?? [];
      return orders.filter(order => ids.includes(order.systemId));
    }),
    delay(0)
  );

  // System name -> every id it's known by (system_info doc id and its own
  // systemId field), filled in when system_info loads below. Orders can
  // reference either, so queries and the client-side filter use all of them.
  private systemIdsByName: { [name: string]: string[] } = {};

  // Every system, from the system_info collection - not derived from
  // whatever orders happen to be loaded, which would leave out any system
  // with no orders in the current result set. Built in the constructor
  // because it needs the injected service.
  systemOptions$: Observable<string[]>;

  // Narrows the System autocomplete's option list as the user types.
  private systemSearchText$ = new BehaviorSubject<string>('');
  filteredSystemOptions$: Observable<string[]>;

  private ordersSub: Subscription;
  private userSub: Subscription;
  private updateCompanySub: Subscription;

  constructor(private dialog: MatDialog, private orderService: OrdersService, private authService: AuthService, private notifierService: NotificationService, private systemInfoService: SystemInfoService) {
    this.rowActionButtons = [
      { icon: 'info', tooltip: 'Order Detail', eventEmitter: this.updateCompany },
      { icon: 'credit_card', tooltip: 'Other orders paid with the same card', eventEmitter: this.sameCard }
    ];

    this.systemOptions$ = this.systemInfoService.systemInfo().pipe(
      take(1),
      map(systems => {
        systems.forEach(system => {
          const name = system['name'];
          if (!name) return;
          const ids = [system['dbid'], system['systemId']].filter(Boolean).map(String);
          this.systemIdsByName[name] = Array.from(new Set([...(this.systemIdsByName[name] ?? []), ...ids]));
        });
        return Object.keys(this.systemIdsByName).sort();
      }),
      shareReplay(1)
    );
    this.filteredSystemOptions$ = combineLatest([this.systemOptions$, this.systemSearchText$]).pipe(
      map(([options, text]) => {
        const term = text.trim().toLowerCase();
        return term ? options.filter(system => system.toLowerCase().includes(term)) : options;
      })
    );
  }

  ngOnInit(): void {
    this.updateCompanySub = this.updateCompany.subscribe(event => {
      if (event.eventData) this.showDetail(event.eventData);
    });
    this.sameCardSub = this.sameCard.subscribe(event => {
      if (event.eventData) this.findSameCard(event.eventData);
    });

    this.fetchByTime(moment().startOf('day').unix(), moment().endOf('day').unix());
  }

  setTimePeriodeEvent(event: MatSelectChange) {
    this.setTimePeriode(event.value);
  }

  setTimePeriode(value) {
    try {

      const now = moment();

      let from;
      let to;
      switch (value) {
        case 'TODAY':
          from = moment(now).startOf('day').unix();
          to = now.endOf('day').unix();
          break;
        case 'YESTERDAY':
          from = moment(now).subtract(1, 'days').startOf('day').unix();
          // from is already unix seconds here - moment(from) would treat it
          // as milliseconds instead (landing in 1970) and silently produce
          // an invalid, inverted date range. moment.unix() reads it correctly.
          to = moment.unix(from).endOf('day').unix();
          break;
        case 'THIS_WEEK':
          from = moment(now).startOf('week').unix();
          to = moment(now).endOf('day').unix();
          break;
        case 'THIS_MONTH':
          from = moment(now).startOf('month').unix();
          to = moment(now).endOf('day').unix();
          break;
        case 'LAST_MONTH':
          from = moment(now).subtract(1, 'month').startOf('month').unix();
          // Same unix-seconds-vs-milliseconds pitfall as YESTERDAY above.
          to = moment.unix(from).endOf('month').unix();
          break;
        case 'CUSTOM':
          return;
          break;
        default:
          throw new Error("Unhandled select time case!")
      }

      this.inputDateFrom = from;
      this.inputDateTo = to;


    } catch (err) {
      console.log(err.message)
    }

  }

  customDateRange = { start: null, end: null };

  setCustomRange(event: MatDatepickerInputEvent<Date>, picker: 'start' | 'end') {
    if (picker === 'start') {
      this.inputDateFrom = moment(event.value).startOf('day').unix();
    } else {
      this.inputDateTo = moment(event.value).endOf('day').unix()
    }
  }

  clearTimeFilter() {
    this.timeSelected = '';
    this.inputDateFrom = null;
    this.inputDateTo = null;
    this.dateFilterActive = false;
    this.fetchRecent();
  }

  setSystemFilter(value: string) {
    this.selectedSystem$.next(value);
    // Reset so the next time the autocomplete panel opens it shows the full
    // list again, instead of still being narrowed by whatever was typed to
    // find this selection. Deliberately doesn't touch appliedSystem$ - just
    // picking a system shouldn't change what's currently shown; that only
    // happens once Search is clicked.
    this.systemSearchText$.next('');
  }

  // The clear (x) icon is an explicit "remove this filter now" action, so
  // unlike setSystemFilter() above it does apply immediately.
  clearSystemFilter() {
    this.selectedSystem$.next('');
    this.appliedSystem$.next('');
    this.systemSearchText$.next('');
  }

  filterSystemOptions(text: string) {
    this.systemSearchText$.next(text);
  }

  // Exact match, no date bound - runs on Enter so a known order number is
  // found regardless of how old it is or which time period is currently
  // selected. Replaces the table's contents with just the match(es) instead
  // of merging them into whatever else was loaded, since System/Time
  // period/Max orders have no meaningful bearing on an exact order lookup
  // (see orderNumberSearchActive, which disables those controls while this
  // is in effect).
  // Backend-allocated order numbers are 8+ digits (2026-09 on); older ones are
  // 13-digit timestamps.
  readonly ORDER_NUMBER_MIN_LENGTH = 8;
  readonly ORDER_NUMBER_MAX_LENGTH = 13;
  readonly orderNumberPattern = `^[0-9]{${this.ORDER_NUMBER_MIN_LENGTH},${this.ORDER_NUMBER_MAX_LENGTH}}$`;

  onOrderNumberInput(value: string) {
    this.orderNumberSearch = value.replace(/\D/g, '');
  }

  applyOrderNumberSearch() {
    const term = this.orderNumberSearch.trim();
    if (!term) return;
    if (term.length < this.ORDER_NUMBER_MIN_LENGTH || term.length > this.ORDER_NUMBER_MAX_LENGTH) {
      this.notifierService.showNotification('info', `Order numbers are ${this.ORDER_NUMBER_MIN_LENGTH}–${this.ORDER_NUMBER_MAX_LENGTH} digits long - "${term}" is ${term.length}.`);
      return;
    }
    this.isLoading = true;
    this.orderService.findOrderByNumber$(term).then(orders => {
      this.isLoading = false;
      this.orderNumberSearchActive = true;
      const rows = orders
        .map(order => {
          try {
            return this.toRow(order);
          } catch (error) {
            console.error('Failed to process a matched order, skipping it:', error);
            return null;
          }
        })
        .filter(row => row !== null);
      this.orderNumberResults$.next(rows);
      if (!rows.length) {
        this.notifierService.showNotification('info', `No order found matching "${term}".`);
      }
    }).catch(error => {
      this.isLoading = false;
      console.error('Order number search failed:', error);
      this.notifierService.showNotification('error', 'Order number search failed. See the browser console for details.');
    });
  }

  clearOrderNumberSearch() {
    this.orderNumberSearch = '';
    this.orderNumberSearchActive = false;
    this.cardSearch = '';
    this.cardSearchLabel = '';
    this.orderNumberResults$.next(null);
  }

  onCardInput(value: string) {
    this.cardSearch = value.replace(/\D/g, '').slice(0, 4);
  }

  // Orders paid with a card ending in these four digits. Many cards share
  // four digits, so the chosen Time period and System narrow the result
  // (both optional); newest first. Replaces the table like an order-number
  // search does.
  applyCardSearch() {
    const last4 = this.cardSearch;
    if (!/^\d{4}$/.test(last4)) {
      this.notifierService.showNotification('info', 'Type the last 4 digits of the card.');
      return;
    }
    const from = this.inputDateFrom ? this.inputDateFrom * 1000 : null;
    const to = this.inputDateTo ? this.inputDateTo * 1000 : null;
    const system = this.selectedSystem$.value;
    const ids = system ? (this.systemIdsByName[system] ?? []) : null;
    const scope = [from && to ? `${moment(from).format('DD.MM.YY')}–${moment(to).format('DD.MM.YY')}` : 'any date', system || 'all systems'].join(', ');
    this.runCardSearch(this.orderService.findOrdersByCardLast4$(last4), o => {
      const t = orderTimeOf(o);
      return (!from || t >= from) && (!to || t <= to) && (!ids || ids.includes(o.systemId));
    }, `Card ending ${last4} (${scope})`);
  }

  // Row button: every order paid with this exact card.
  findSameCard(order: any) {
    if (!order.cardFingerprint) {
      this.notifierService.showNotification('info', 'No card stored on this order (Vipps, credit or an older order).');
      return;
    }
    this.cardSearch = order.cardLast4 || '';
    this.runCardSearch(this.orderService.findOrdersByCardFingerprint$(order.cardFingerprint), () => true,
      `Same card as order ${order.orderNo || order.orderId}: ${cardLabel(order)}`);
  }

  private runCardSearch(lookup: Promise<any[]>, keep: (o: any) => boolean, label: string) {
    this.isLoading = true;
    lookup.then(orders => {
      this.isLoading = false;
      const rows = orders.filter(keep)
        .sort((a, b) => orderTimeOf(b) - orderTimeOf(a))
        .map(order => {
          try { return this.toRow(order); } catch { return null; }
        })
        .filter(row => row !== null);
      this.orderNumberSearch = '';
      this.orderNumberSearchActive = true;
      this.cardSearchLabel = `${label}: ${rows.length} order${rows.length === 1 ? '' : 's'}`;
      this.orderNumberResults$.next(rows);
      if (!rows.length) this.notifierService.showNotification('info', 'No orders found for that card.');
    }).catch(error => {
      this.isLoading = false;
      console.error('Card search failed:', error);
      this.notifierService.showNotification('error', 'Card search failed. See the browser console for details.');
    });
  }

  private toRow(data: object) {
    // Historical orders reached via the any-day/system/search queries can
    // predate schema changes (e.g. orderDetail stored as something other
    // than an array) - guard against that instead of letting one malformed
    // document throw and abort the whole batch (see mapSnapshotDocs below).
    const orderDetail = Array.isArray(data['orderDetail']) ? data['orderDetail'] : [];
    return {
      ...data,
      // Same normalisation as the live Orders page, so the name matches the
      // spaced spelling used by system_info and the System filter.
      name: typeof data['name'] === 'string' ? data['name'].replace(/_/g, ' ') : data['name'],
      product: orderDetail.map(detail => (detail?.name ?? '') + " (" + (detail?.price ?? '') + ")").join(", "),
      timestamp: data['orderDate'] ? moment(data['orderDate']).format('DD-MM-YY HH:mm:ss') : '',
      user: data['user'] ? data['user']['name'] : "Unknown",
      card: cardLabel(data)
    }
  }

  // A single malformed document (e.g. from an older schema) must not throw
  // inside the snapshot's next() handler - that would abort the whole batch
  // silently, since a synchronous throw there isn't routed to the error()
  // handler, leaving the spinner stuck with no data and no visible error.
  private mapSnapshotDocs(docs: { id: string, data: () => unknown }[]) {
    return docs
      .map(doc => {
        try {
          return this.toRow(doc.data() as object);
        } catch (error) {
          console.error('Failed to process order document, skipping it:', doc.id, error);
          return null;
        }
      })
      .filter(row => row !== null);
  }

  private onFetchError(error) {
    console.error('Order Report query failed:', error);
    this.isLoading = false;
    const isMissingIndex = error?.code === 'failed-precondition' || /index/i.test(error?.message || '');
    this.notifierService.showNotification('error', isMissingIndex
      ? 'This search needs a Firestore index that has not been created yet. Check the browser console for a link to create it.'
      : 'Failed to load orders. See the browser console for details.');
  }

  private applySnapshot(sp: { docs: { id: string, data: () => unknown }[] }) {
    try {
      this.rawOrders$.next(this.mapSnapshotDocs(sp.docs));
    } finally {
      this.isLoading = false;
    }
  }

  private fetchByTime(dateFrom, dateTo) {
    this.isLoading = true;
    if (this.ordersSub) this.ordersSub.unsubscribe();
    this.ordersSub = this.orderService.getOrdersByTime$(dateFrom, dateTo, this.maxOrders).get().subscribe({
      next: sp => this.applySnapshot(sp),
      error: error => this.onFetchError(error)
    });
  }

  // A system picked from the list always has ids (see systemIdsByName);
  // an empty list would make Firestore's `in` filter throw.
  private systemIdsOrWarn(system: string): string[] | null {
    const ids = this.systemIdsByName[system] ?? [];
    if (!ids.length) {
      this.notifierService.showNotification('error', `No system id found for "${system}".`);
      return null;
    }
    return ids;
  }

  private fetchByTimeAndSystem(dateFrom, dateTo, system: string) {
    const ids = this.systemIdsOrWarn(system);
    if (!ids) return;
    this.isLoading = true;
    if (this.ordersSub) this.ordersSub.unsubscribe();
    this.ordersSub = this.orderService.getOrdersByTimeAndSystem$(dateFrom, dateTo, ids, this.maxOrders).get().subscribe({
      next: sp => this.applySnapshot(sp),
      error: error => this.onFetchError(error)
    });
  }

  private fetchRecent() {
    this.isLoading = true;
    if (this.ordersSub) this.ordersSub.unsubscribe();
    this.ordersSub = this.orderService.getRecentOrders$(this.maxOrders).get().subscribe({
      next: sp => this.applySnapshot(sp),
      error: error => this.onFetchError(error)
    });
  }

  private fetchBySystem(system: string) {
    const ids = this.systemIdsOrWarn(system);
    if (!ids) return;
    this.isLoading = true;
    if (this.ordersSub) this.ordersSub.unsubscribe();
    this.ordersSub = this.orderService.getOrdersBySystem$(ids, this.maxOrders).get().subscribe({
      next: sp => this.applySnapshot(sp),
      error: error => this.onFetchError(error)
    });
  }

  onSubmit() {
    // Commits the staged System choice - this is the one place it actually
    // takes effect on what's displayed (see appliedSystem$/setSystemFilter).
    const system = this.selectedSystem$.value;
    this.appliedSystem$.next(system);

    // An explicit time period/custom range was chosen - apply it (also
    // picks up the current Max orders value). If a System is also selected,
    // query it scoped to both together instead of pulling maxOrders worth
    // of every system in that range and filtering client-side - otherwise a
    // busy network can fill the limit with other systems' orders before
    // this one's even get read.
    if (this.inputDateFrom && this.inputDateTo) {
      this.dateFilterActive = true;
      if (system) {
        this.fetchByTimeAndSystem(this.inputDateFrom, this.inputDateTo, system);
      } else {
        this.fetchByTime(this.inputDateFrom, this.inputDateTo);
      }
      return;
    }

    // No date range chosen: previously this just did nothing, so changing
    // Max orders with no filter/search active had no way to take effect.
    // Re-run whatever's currently in scope - System filter if one's picked,
    // otherwise the same "today" default the page loads with - using the
    // current Max orders value.
    if (system) {
      this.fetchBySystem(system);
      return;
    }
    this.fetchByTime(moment().startOf('day').unix(), moment().endOf('day').unix());
  }

  showDetail(data) {
    if (this.userSub) this.userSub.unsubscribe();
    this.userSub = this.authService.getUser$().subscribe(userInfo => {
      const userRoles = Object.keys(userInfo || {}).filter(key => userInfo[key] === true);

      this.dialog.open(OrderDetailPopup, {
        width: '550px',
        disableClose: true,
        data: {
          ...data,
          roles: userRoles
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.ordersSub) this.ordersSub.unsubscribe();
    if (this.userSub) this.userSub.unsubscribe();
    if (this.updateCompanySub) this.updateCompanySub.unsubscribe();
    if (this.sameCardSub) this.sameCardSub.unsubscribe();
  }
}
